import { Box, Button, Flex, Link, SimpleGrid, Spinner, Stack, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { driveFolderUrl } from 'utils/driveFolderUrl';
import {
  explanationWithoutEmbeddedFileSummary,
  extractFileSummarySection,
  parseDocumentSummaryEntries,
  splitExplanationNotesAndFlags,
} from './formatAccountingExtractionDisplay';
import {
  fmtAccountingSnapshotValue,
  formatExtractElapsed,
} from './formatAccountingSnapshot';

function SnapshotField({ label, field, sourceLabel }) {
  if (field?.value == null) {
    return null;
  }
  return (
    <Box border="1px solid" borderColor="purple.100" borderRadius="md" p={3} bg="white">
      <Text fontSize="xs" color="gray.500" mb={1}>
        {label}
      </Text>
      <Text fontWeight="semibold">{fmtAccountingSnapshotValue(label, field.value)}</Text>
      {field.reason ? (
        <Text fontSize="xs" color="gray.600" mt={1}>
          {field.reason}
        </Text>
      ) : null}
      {sourceLabel ? (
        <Text fontSize="xs" color="purple.600" mt={1}>
          {sourceLabel}
        </Text>
      ) : null}
    </Box>
  );
}

function DocumentSummaryList({ documentSummary }) {
  const entries = useMemo(
    () => parseDocumentSummaryEntries(documentSummary),
    [documentSummary]
  );
  if (!entries.length) {
    return null;
  }
  return (
    <Box mb={3} p={3} borderRadius="md" bg="purple.50" border="1px solid" borderColor="purple.100">
      <Text fontSize="sm" fontWeight="semibold" mb={2}>
        What these files contain
      </Text>
      <Stack spacing={2}>
        {entries.map((entry, index) => (
          <Box
            key={`${entry.title || 'row'}-${index}`}
            border="1px solid"
            borderColor="purple.100"
            borderRadius="md"
            p={2}
            bg="white"
          >
            {entry.title ? (
              <Text fontSize="xs" fontWeight="semibold" color="purple.700" mb={1}>
                {entry.title}
              </Text>
            ) : null}
            <Text fontSize="sm" color="gray.700">
              {entry.body}
            </Text>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

DocumentSummaryList.propTypes = {
  documentSummary: PropTypes.string,
};

function ExtractionNotesPanel({ explanation }) {
  const { notes, flags } = useMemo(
    () => splitExplanationNotesAndFlags(explanation),
    [explanation]
  );
  if (!notes && !flags) {
    return null;
  }
  return (
    <Stack spacing={3} mb={3}>
      {notes ? (
        <Box p={3} borderRadius="md" bg="gray.50" border="1px solid" borderColor="gray.200">
          <Text fontSize="sm" fontWeight="semibold" mb={1}>
            Extraction notes
          </Text>
          <Text fontSize="sm" whiteSpace="pre-wrap">
            {notes}
          </Text>
        </Box>
      ) : null}
      {flags ? (
        <Box p={3} borderRadius="md" bg="orange.50" border="1px solid" borderColor="orange.200">
          <Text fontSize="sm" fontWeight="semibold" mb={1}>
            Flags
          </Text>
          <Text fontSize="sm" whiteSpace="pre-wrap">
            {flags}
          </Text>
        </Box>
      ) : null}
    </Stack>
  );
}

ExtractionNotesPanel.propTypes = {
  explanation: PropTypes.string,
};

function DriveFolderLink({ label, folderId, folderName }) {
  const url = driveFolderUrl(folderId);
  if (!url) {
    return null;
  }
  return (
    <Text fontSize="sm">
      {label}:{' '}
      <Link href={url} isExternal color="purple.600">
        {folderName || label}
      </Link>
    </Text>
  );
}

function AssistResultsPanel({
  accountingSnapshot,
  assistSession,
  extractJob,
  extractUsage,
  cohortApplied,
  onDeleteOpenAIFiles,
  deletingOpenAIFiles,
  onCancelExtract,
  cancellingExtract,
}) {
  const hasSnapshot =
    accountingSnapshot &&
    (accountingSnapshot.gpm ||
      accountingSnapshot.opex ||
      accountingSnapshot.beginCash ||
      accountingSnapshot.revenueGrowthYoy ||
      accountingSnapshot.totalAssets ||
      accountingSnapshot.totalLiabilities ||
      accountingSnapshot.safe ||
      accountingSnapshot.debt ||
      accountingSnapshot.balanceSheet ||
      accountingSnapshot.cashBalance ||
      accountingSnapshot.runway);

  const documentSummary = accountingSnapshot?.documentSummary?.trim?.() || '';
  const notesExplanation = useMemo(() => {
    const raw = accountingSnapshot?.explanation || '';
    const stripped = explanationWithoutEmbeddedFileSummary(raw, documentSummary);
    if (documentSummary) {
      return stripped;
    }
    const { remainder } = extractFileSummarySection(raw);
    return remainder || stripped;
  }, [accountingSnapshot?.explanation, documentSummary]);

  const displayDocumentSummary = useMemo(() => {
    if (documentSummary) {
      return documentSummary;
    }
    const { summary } = extractFileSummarySection(accountingSnapshot?.explanation || '');
    return summary;
  }, [documentSummary, accountingSnapshot?.explanation]);

  const extracting =
    extractJob?.status === 'running' || extractJob?.status === 'pending';
  const failed = extractJob?.status === 'failed';
  const cancelled = extractJob?.status === 'cancelled';
  const [elapsed, setElapsed] = useState(null);

  const customerFolderId = assistSession?.customerFolderId;
  const customerFolderName = assistSession?.customerFolderName;
  const dataFolderId = assistSession?.dataFolderId;
  const hasDriveFolders =
    Boolean(customerFolderId) ||
    Boolean(dataFolderId && dataFolderId !== customerFolderId);

  useEffect(() => {
    if (!extracting) {
      setElapsed(null);
      return undefined;
    }
    const tick = () => setElapsed(formatExtractElapsed(extractJob?.startedAt));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [extracting, extractJob?.startedAt]);

  if (
    !hasSnapshot &&
    !displayDocumentSummary &&
    !notesExplanation &&
    !extracting &&
    !failed &&
    !cancelled &&
    !cohortApplied &&
    !hasDriveFolders &&
    !extractUsage
  ) {
    return null;
  }

  const sourceFiles = accountingSnapshot?.sourceFiles || [];
  const sourceLabel =
    sourceFiles.length === 1
      ? `From ${sourceFiles[0].name}`
      : sourceFiles.length > 1
        ? `From ${sourceFiles.length} Drive files`
        : null;

  return (
    <Box className="card" mt={4}>
      <Flex justify="space-between" align="center" mb={3} gap={2} flexWrap="wrap">
        <Text fontWeight="semibold">Extraction results</Text>
        {onDeleteOpenAIFiles ? (
          <Button
            size="sm"
            variant="outline"
            colorScheme="purple"
            onClick={onDeleteOpenAIFiles}
            isLoading={deletingOpenAIFiles}
          >
            Delete OpenAI files
          </Button>
        ) : null}
      </Flex>
      {extracting ? (
        <Flex align="center" gap={3} mb={3} flexWrap="wrap">
          <Spinner size="sm" color="purple.500" />
          <Box flex={1}>
            <Text fontSize="sm" color="gray.700" fontWeight="medium">
              {extractJob?.stageMessage || 'Extracting accounting files…'}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {extractJob?.fileCount > 0 && extractJob?.fileIndex > 0
                ? `File ${extractJob.fileIndex} of ${extractJob.fileCount}${
                    extractJob.currentFileName
                      ? `: ${extractJob.currentFileName}`
                      : ''
                  } · `
                : ''}
              {elapsed ? `Running for ${elapsed}` : 'Starting…'}
              {extractJob?.status === 'running' ? ' · still in progress' : ''}
            </Text>
          </Box>
          {onCancelExtract ? (
            <Button
              size="sm"
              variant="outline"
              colorScheme="purple"
              onClick={onCancelExtract}
              isLoading={cancellingExtract}
            >
              Cancel extraction
            </Button>
          ) : null}
        </Flex>
      ) : null}
      {failed ? (
        <Text fontSize="sm" color="red.600" mb={3}>
          {extractJob.error || 'Extraction failed. Try fewer or smaller accounting files.'}
        </Text>
      ) : null}
      {cancelled ? (
        <Text fontSize="sm" color="gray.600" mb={3}>
          Extraction was cancelled.
        </Text>
      ) : null}
      {hasDriveFolders ? (
        <Box mb={3}>
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Source folder
          </Text>
          {dataFolderId && dataFolderId !== customerFolderId ? (
            <DriveFolderLink
              label="Data folder"
              folderId={dataFolderId}
              folderName={customerFolderName ? `${customerFolderName} / Data` : 'Data folder'}
            />
          ) : null}
          <DriveFolderLink
            label="Customer folder"
            folderId={customerFolderId}
            folderName={customerFolderName}
          />
        </Box>
      ) : null}
      <DocumentSummaryList documentSummary={displayDocumentSummary} />
      <ExtractionNotesPanel explanation={notesExplanation} />
      {hasSnapshot ? (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <SnapshotField label="GPM (%)" field={accountingSnapshot.gpm} sourceLabel={sourceLabel} />
          <SnapshotField label="Monthly OPEX" field={accountingSnapshot.opex} />
          <SnapshotField label="Begin cash" field={accountingSnapshot.beginCash} />
          <SnapshotField
            label="Revenue growth YoY (%)"
            field={accountingSnapshot.revenueGrowthYoy}
          />
          <SnapshotField label="Total assets" field={accountingSnapshot.totalAssets} />
          <SnapshotField
            label="Total liabilities"
            field={accountingSnapshot.totalLiabilities}
          />
          <SnapshotField label="SAFE" field={accountingSnapshot.safe} />
          <SnapshotField label="Debt" field={accountingSnapshot.debt} />
          <SnapshotField
            label="Balance sheet ratio"
            field={accountingSnapshot.balanceSheet}
          />
          <SnapshotField label="Cash / OPEX" field={accountingSnapshot.cashBalance} />
          <SnapshotField label="Net burn ($/mo)" field={accountingSnapshot.netBurn} />
          <SnapshotField label="Runway (months)" field={accountingSnapshot.runway} />
        </SimpleGrid>
      ) : null}
      {extractUsage ? (
        <Text className="chat-usage" fontSize="xs" color="gray.500" mt={3}>
          {extractUsage}
        </Text>
      ) : null}
      {cohortApplied ? (
        <Text fontSize="sm" color="green.600" mt={3}>
          Cohort triangle applied to the model.
        </Text>
      ) : null}
    </Box>
  );
}

AssistResultsPanel.propTypes = {
  accountingSnapshot: PropTypes.object,
  assistSession: PropTypes.object,
  extractJob: PropTypes.object,
  extractUsage: PropTypes.string,
  cohortApplied: PropTypes.bool,
  onDeleteOpenAIFiles: PropTypes.func,
  deletingOpenAIFiles: PropTypes.bool,
  onCancelExtract: PropTypes.func,
  cancellingExtract: PropTypes.bool,
};

export default AssistResultsPanel;
